/**
 * EffectivePermissionPanel (S3.1) — "what can this user do, and why".
 *
 * The least-privilege PREVIEW surface (plan §Slice 3.1): renders
 * `resolveEffectiveAccess().sources` for one governance user, resolved LIVE
 * client-side over the editable catalogs — the sanctioned client-side consumer
 * of the very same pure resolver the MSW `/auth/me` stamp runs (plan §Resolver
 * contract, portability split), so the panel and the stamped snapshot can never
 * drift (parity asserted by the S3.1 vitest against `stampEffectiveAccess`).
 * Pairs with (does not replace) the DEV view-as identity switch (S1.5).
 *
 * Render contract:
 *   • every grant shows its HUMAN `governance.permissions.*` label — the raw
 *     dotted code NEVER renders (acceptance-asserted; label key derived by the
 *     shared S2.2 `permissionLabelKey`, no second map);
 *   • every grant is provenance-tagged from `GrantSource[]`, each kind a
 *     DISTINCT full-word tag (never a bare glyph, never colour-only):
 *     direct → 來自角色 / inherited-junior → 繼承自 (S2.2's branches glyph
 *     vocabulary) / implication → 由…涵蓋 (subnode glyph);
 *   • catalog + extended permissions the user does NOT hold render as
 *     no-access rows WITH the reason and the granting-role remediation hint
 *     (`explainDecision`) — e.g. Melissa's 檢視訂單控管 (`occ.view`). NOTE for
 *     `ux-review`: like S2.2's inherited cells, these read-only no-access rows
 *     are the sanctioned exception to hide-don't-disable — this panel is an
 *     INSPECTION surface whose whole job is explaining absence (it replaced
 *     the dropped "Check access" box, plan §Slice 3.2);
 *   • Axis-B reach renders as the read-only `ReadinessMeter` "sees N 組織單位"
 *     (S2.4's shared vocabulary), each visible org textually marked 直營/加盟;
 *   • the `SourceStampTag` header marks the whole panel as a derived READ
 *     projection (resolved-at stamp) — nothing here is editable; write-back
 *     lives in RolesTab (grants) and the assignment modal (scope).
 */

import { useMemo } from 'react';
import { Space, Tag, Typography, theme } from 'antd';
import { BranchesOutlined, StopOutlined, SubnodeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import SourceStampTag from '@/components/common/SourceStampTag';
import ReadinessMeter from '@/components/common/ReadinessMeter';
import { EXTENDED_PERMISSIONS, PERMISSION_CATALOG } from '@/constants/routePermissions';
import { explainDecision, fromProfile, resolveEffectiveAccess } from '@/lib/rbac';
import type { GrantSource } from '@/lib/rbac';
import { permissionLabelKey } from './PermissionMatrixGrid';
import type { GovernanceRole, GovernanceUser, OrganisationUnit } from './model';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- the pure preview-model
   builder is exported for the S3.1 node-env parity test (house precedent:
   PermissionMatrixGrid / OrgScopeAssignmentModal pure exports). */

/**
 * Module of a code by its first dotted segment — the SAME derivation as the
 * S2.2 grid's private `moduleOfCode` (`products.*` → masterData,
 * `finance.costBasis.edit` → finance, un-prefixed structural grants like
 * `crossFranchiseView` → system). Kept local because that helper is not
 * exported and `PermissionMatrixGrid.tsx` is outside this slice's file set —
 * followUp recorded to unify on one export.
 */
const MODULE_BY_PREFIX: ReadonlyMap<string, string> = new Map(
  PERMISSION_CATALOG.flatMap((module) =>
    module.permissions.map((code) => [code.split('.')[0], module.moduleKey] as const)
  )
);

function moduleForCode(code: string): string {
  return MODULE_BY_PREFIX.get(code.split('.')[0]) ?? 'system';
}

/** One HELD grant — the code plus why it is held (`resolveEffectiveAccess().sources`). */
export interface EffectiveGrantRowModel {
  code: string;
  sources: readonly GrantSource[];
}

/** One NOT-held permission — the code plus the granting-role remediation hint. */
export interface EffectiveMissingRowModel {
  code: string;
  /** Active roles whose own effective grants include the code (`explainDecision`). */
  grantingRoleIds: readonly string[];
}

export interface EffectiveModuleModel {
  moduleKey: string;
  held: readonly EffectiveGrantRowModel[];
  missing: readonly EffectiveMissingRowModel[];
}

/** The panel's whole pure view-model — built by `buildEffectivePreview`. */
export interface EffectivePreviewModel {
  modules: readonly EffectiveModuleModel[];
  /** Axis-A: `resolveEffectiveAccess().grants.size`. */
  grantCount: number;
  /** Axis-B: resolved visible org ids (insertion order of the resolver). */
  visibleOrgIds: readonly string[];
}

/**
 * Build the panel's view-model through the shared pure resolver — grants in
 * catalog order first, then non-catalog held codes (implication phantoms,
 * extended grants, structural `crossFranchiseView`) appended alphabetically to
 * their module; catalog + extended codes NOT held become no-access rows with
 * `explainDecision`'s granting-role hint. Pure and I/O-free, so the node-env
 * S3.1 test asserts it verbatim against the MSW `stampEffectiveAccess` output.
 */
export function buildEffectivePreview(
  user: GovernanceUser,
  roles: readonly GovernanceRole[],
  orgs: readonly OrganisationUnit[]
): EffectivePreviewModel {
  const input = { identity: fromProfile(user), roles, orgs };
  const access = resolveEffectiveAccess(input);

  const held = new Map<string, EffectiveGrantRowModel[]>();
  const missing = new Map<string, EffectiveMissingRowModel[]>();
  const push = <T,>(map: Map<string, T[]>, moduleKey: string, row: T): void => {
    const list = map.get(moduleKey);
    if (list) list.push(row);
    else map.set(moduleKey, [row]);
  };
  const rowFor = (code: string): EffectiveGrantRowModel => ({
    code,
    sources: access.sources.get(code) ?? [],
  });

  // 1 — catalog codes, in catalog order: held or no-access-with-reason.
  const catalogCodes = new Set<string>();
  for (const module of PERMISSION_CATALOG) {
    for (const code of module.permissions) {
      catalogCodes.add(code);
      if (access.grants.has(code)) {
        push(held, module.moduleKey, rowFor(code));
      } else {
        push(missing, module.moduleKey, {
          code,
          grantingRoleIds: explainDecision(input, code).grantingRoleIds,
        });
      }
    }
  }

  // 2 — extended permissions (business-action grants outside the route catalog).
  for (const code of EXTENDED_PERMISSIONS) {
    catalogCodes.add(code);
    if (access.grants.has(code)) {
      push(held, moduleForCode(code), rowFor(code));
    } else {
      push(missing, moduleForCode(code), {
        code,
        grantingRoleIds: explainDecision(input, code).grantingRoleIds,
      });
    }
  }

  // 3 — remaining held codes (implication phantoms + structural grants),
  //     alphabetical for a deterministic render.
  const extras = [...access.grants].filter((code) => !catalogCodes.has(code)).sort();
  for (const code of extras) {
    push(held, moduleForCode(code), rowFor(code));
  }

  const modules: EffectiveModuleModel[] = PERMISSION_CATALOG.map((module) => ({
    moduleKey: module.moduleKey,
    held: held.get(module.moduleKey) ?? [],
    missing: missing.get(module.moduleKey) ?? [],
  })).filter((module) => module.held.length > 0 || module.missing.length > 0);

  return {
    modules,
    grantCount: access.grants.size,
    visibleOrgIds: [...access.visibleOrgIds],
  };
}

/* eslint-enable react-refresh/only-export-components */

export interface EffectivePermissionPanelProps {
  user: GovernanceUser;
  roles: GovernanceRole[];
  organisations: OrganisationUnit[];
}

const EffectivePermissionPanel = ({
  user,
  roles,
  organisations,
}: EffectivePermissionPanelProps) => {
  const { t } = useTranslation('governance');
  const { token } = theme.useToken();

  // One memo mints the model AND its resolved-at stamp together — the stamp is
  // honest provenance for exactly this projection (UI-layer clock; the resolver
  // itself stays clock-free).
  const { model, resolvedAt } = useMemo(
    () => ({
      model: buildEffectivePreview(user, roles, organisations),
      resolvedAt: new Date().toISOString(),
    }),
    [user, roles, organisations]
  );

  const roleNameById = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles]
  );
  const orgById = useMemo(
    () => new Map(organisations.map((org) => [org.id, org])),
    [organisations]
  );

  /** Human `governance.permissions.*` label — the ONLY way a code renders. */
  const permissionLabel = (code: string): string =>
    t(`permissions.${permissionLabelKey(code)}`);

  /** One full-word provenance tag per grant source — distinct kind, never colour-only. */
  const sourceTag = (source: GrantSource, index: number) => {
    if (source.kind === 'direct') {
      const role = roleNameById.get(source.roleId) ?? source.roleId;
      return (
        <Tag key={`direct-${index}`} color="purple" style={{ marginInlineEnd: 0 }}>
          {t('effective.fromRole', { role })}
        </Tag>
      );
    }
    if (source.kind === 'inherited-junior') {
      const role = roleNameById.get(source.roleId) ?? source.roleId;
      return (
        <Tag
          key={`inherited-${index}`}
          icon={<BranchesOutlined />}
          style={{ marginInlineEnd: 0 }}
        >
          {t('effective.inheritedFrom', { role })}
        </Tag>
      );
    }
    return (
      <Tag key={`implied-${index}`} icon={<SubnodeOutlined />} style={{ marginInlineEnd: 0 }}>
        {t('effective.viaImplication', { permission: permissionLabel(source.ofPermission) })}
      </Tag>
    );
  };

  const rowStyle = {
    padding: `${token.paddingXS}px 0`,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  } as const;

  return (
    <div>
      {/* Header — who, plus the derived-projection provenance stamp. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: token.marginSM,
          marginBottom: token.marginXS,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Text strong style={{ display: 'block' }}>
            {user.name}
          </Text>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {user.email}
          </Text>
        </div>
        <SourceStampTag sourceLabel={t('effective.sourceLive')} syncedAt={resolvedAt} />
      </div>

      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginMD }}
      >
        {t('effective.hint')}
      </Text>

      {/* Roles held. */}
      <Text style={{ display: 'block', marginBottom: token.marginXXS }} strong>
        {t('effective.rolesHeld')}
      </Text>
      {user.roleNames.length > 0 ? (
        <Space wrap size={4} style={{ marginBottom: token.marginSM }}>
          {user.roleNames.map((name) => (
            <Tag key={name} color="purple" style={{ marginInlineEnd: 0 }}>
              {name}
            </Tag>
          ))}
        </Space>
      ) : (
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginSM }}
        >
          {t('effective.noRoles')}
        </Text>
      )}

      <Text type="secondary" style={{ display: 'block', marginBottom: token.marginXS }}>
        {t('effective.grantCount', { count: model.grantCount })}
      </Text>

      {/* Axis A — per-module grants with provenance, then no-access rows. */}
      {model.modules.map((module) => (
        <div key={module.moduleKey}>
          <Text
            strong
            style={{ display: 'block', marginTop: token.marginMD, marginBottom: token.marginXXS }}
          >
            {t(`permissionModules.${module.moduleKey}`)}
          </Text>

          {module.held.map((row) => (
            <div
              key={row.code}
              style={{
                ...rowStyle,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: token.marginSM,
              }}
            >
              <Text>{permissionLabel(row.code)}</Text>
              <Space wrap size={4} style={{ justifyContent: 'flex-end' }}>
                {row.sources.map((source, index) => sourceTag(source, index))}
              </Space>
            </div>
          ))}

          {module.missing.map((row) => {
            const grantingNames = row.grantingRoleIds.map(
              (roleId) => roleNameById.get(roleId) ?? roleId
            );
            return (
              <div key={row.code} style={rowStyle}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: token.marginSM,
                  }}
                >
                  <Text type="secondary">{permissionLabel(row.code)}</Text>
                  <Tag icon={<StopOutlined />} style={{ marginInlineEnd: 0 }}>
                    {t('effective.noAccess')}
                  </Tag>
                </div>
                <Text
                  style={{
                    display: 'block',
                    fontSize: token.fontSizeSM,
                    color: token.colorTextTertiary,
                  }}
                >
                  {t('effective.noAccessReason', { permission: permissionLabel(row.code) })}
                  {' — '}
                  {grantingNames.length > 0
                    ? t('effective.wouldGrant', { roles: grantingNames.join(', ') })
                    : t('effective.noGrantingRole')}
                </Text>
              </div>
            );
          })}
        </div>
      ))}

      {/* Axis B — the resolved reach, S2.4's shared "sees N 組織單位" vocabulary. */}
      <div style={{ marginTop: token.marginLG }}>
        {model.visibleOrgIds.length > 0 ? (
          <ReadinessMeter
            title={t('effective.reachTitle')}
            ready
            statusText={t('assignment.reach', { count: model.visibleOrgIds.length })}
            items={model.visibleOrgIds.map((orgId) => {
              const org = orgById.get(orgId);
              return {
                key: orgId,
                label: org?.name ?? orgId,
                // Textual 直營/加盟 marker — a franchise compartment is never
                // colour-only (S2.4 picker doctrine).
                detail: org ? t(`model.${org.model}`) : undefined,
                complete: true,
              };
            })}
          />
        ) : (
          <>
            <Text strong style={{ display: 'block', marginBottom: token.marginXXS }}>
              {t('effective.reachTitle')}
            </Text>
            <Text type="secondary" style={{ display: 'block', fontSize: token.fontSizeSM }}>
              {t('effective.noScope')}
            </Text>
          </>
        )}
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginTop: token.marginXS }}
        >
          {t('effective.sharedMasterData')}
        </Text>
      </div>
    </div>
  );
};

export default EffectivePermissionPanel;
